import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Add20Regular,
  ArrowDownload20Regular,
  Search20Regular,
  BuildingShop24Regular,
  Mail24Regular,
  Call24Regular,
  TextBulletListSquare24Regular,
  Edit20Regular,
  ArrowLeft24Regular,
  BuildingBank20Regular,
} from "@fluentui/react-icons";
import { PageHeader } from "../../components/PageHeader";
import { ExportarFornecedoresModal } from "../../components/modals/ExportarFornecedoresModal";
import { DuplicateSuppliersModal } from "./DuplicateSuppliersModal";
import {
  ApiError,
  getSuppliers,
  type Supplier,
} from "../../fornecedores/fornecedoresApi";

type CategoriaFornecedor =
  | "Materiais"
  | "Serviços"
  | "Equipamentos";

interface FornecedorView {
  id: number;
  nome: string;
  categoria?: string | null;
  condicoes?: string | null;
  contacto?: string | null;
  email?: string | null;
  telefone?: string | null;
  comprasRealizadas: number;
  notas?: string | null;
  logo?: string | null;
}

const filterOptions: Array<CategoriaFornecedor | "Todos"> = [
  "Todos",
  "Materiais",
  "Serviços",
  "Equipamentos",
];

const parseErrorMessage = (error: unknown): string => {
  if (error instanceof ApiError) {
    if (error.status === 401) {
      return "Sessão expirada. Por favor, autentique-se novamente.";
    }
    if (error.status === 403) {
      return "Sem permissões para consultar fornecedores.";
    }
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return "Não foi possível carregar os fornecedores.";
};

const mapSupplierToView = (supplier: Supplier): FornecedorView => {
  return {
    id: supplier.id,
    nome: supplier.name,
    categoria: supplier.category ?? null,
    condicoes: supplier.commercialTerms ?? null,
    contacto: supplier.contactName ?? null,
    email: supplier.email ?? null,
    telefone: supplier.phoneNumber ?? null,
    comprasRealizadas: supplier.purchasesCount ?? 0,
    notas: supplier.notes ?? null,
    logo: supplier.logoUrl ?? null,
  };
};

const formatOptional = (value?: string | null): string =>
  value && value.trim() ? value : "—";

const getCategoriaBadgeClass = (categoria?: string | null) => {
  switch (categoria) {
    case "Materiais":
      return "bg-chart-1/10 text-chart-1";
    case "Serviços":
      return "bg-chart-2/10 text-chart-2";
    case "Equipamentos":
      return "bg-chart-3/10 text-chart-3";
    default:
      return "bg-muted text-muted-foreground";
  }
};

const getInitials = (nome: string) => {
  return nome
    .split(" ")
    .map((word) => word[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
};

export function FornecedoresView() {
  const navigate = useNavigate();
  const [fornecedores, setFornecedores] = useState<
    FornecedorView[]
  >([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(
    null,
  );
  const [selectedFornecedorId, setSelectedFornecedorId] =
    useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedFilter, setSelectedFilter] = useState<
    CategoriaFornecedor | "Todos"
  >("Todos");
  const [isMobileDetailOpen, setIsMobileDetailOpen] =
    useState(false);
  const [isExportModalOpen, setIsExportModalOpen] =
    useState(false);
  const [isDuplicatesModalOpen, setIsDuplicatesModalOpen] =
    useState(false);

  const loadSuppliers = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);

    try {
      const response = await getSuppliers();
      const mapped = response.map(mapSupplierToView);
      setFornecedores(mapped);
      setSelectedFornecedorId((current) => {
        if (current && mapped.some((item) => item.id === current)) {
          return current;
        }
        return mapped[0]?.id ?? null;
      });
    } catch (error) {
      setFornecedores([]);
      setSelectedFornecedorId(null);
      setLoadError(parseErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSuppliers();
  }, [loadSuppliers]);

  const filteredFornecedores = useMemo(
    () =>
      fornecedores.filter((fornecedor) => {
        const matchesSearch =
          fornecedor.nome
            .toLowerCase()
            .includes(searchQuery.toLowerCase()) ||
          (fornecedor.contacto ?? "")
            .toLowerCase()
            .includes(searchQuery.toLowerCase());
        const matchesFilter =
          selectedFilter === "Todos"
            ? true
            : fornecedor.categoria === selectedFilter;

        return matchesSearch && matchesFilter;
      }),
    [fornecedores, searchQuery, selectedFilter],
  );

  useEffect(() => {
    if (isLoading) return;

    if (filteredFornecedores.length === 0) {
      setSelectedFornecedorId(null);
      return;
    }

    const hasSelection = filteredFornecedores.some(
      (entry) => entry.id === selectedFornecedorId,
    );
    if (!hasSelection) {
      setSelectedFornecedorId(filteredFornecedores[0].id);
    }
  }, [filteredFornecedores, isLoading, selectedFornecedorId]);

  const selectedFornecedor = filteredFornecedores.find(
    (f) => f.id === selectedFornecedorId,
  );

  const actions = (
    <>
      <button
        className="px-4 py-2 bg-secondary hover:bg-accent border border-border rounded transition-colors flex items-center gap-2 min-h-[44px]"
        style={{ borderRadius: "var(--radius-button)" }}
        onClick={() => setIsDuplicatesModalOpen(true)}
      >
        <span>Ver duplicados</span>
      </button>
      <button
        className="px-4 py-2 bg-secondary hover:bg-accent border border-border rounded transition-colors flex items-center gap-2 min-h-[44px]"
        style={{ borderRadius: "var(--radius-button)" }}
        onClick={() => setIsExportModalOpen(true)}
      >
        <ArrowDownload20Regular className="w-5 h-5" />
        <span className="hidden sm:inline">Exportar</span>
      </button>
      <button
        className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded transition-colors flex items-center gap-2 min-h-[44px]"
        style={{
          borderRadius: "var(--radius-button)",
          height: "var(--button-height)",
        }}
        onClick={() => navigate("/fornecedores/novo")}
      >
        <Add20Regular className="w-5 h-5" />
        Novo Fornecedor
      </button>
    </>
  );

  const handleFornecedorSelect = (fornecedorId: number) => {
    setSelectedFornecedorId(fornecedorId);
    setIsMobileDetailOpen(true);
  };

  const handleMobileBack = () => {
    setIsMobileDetailOpen(false);
  };

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title="Fornecedores"
        subtitle="Gestão de fornecedores de materiais, serviços e equipamentos"
        actions={actions}
      />

      <div className="flex-1 flex overflow-hidden">
        <div
          className={`${
            isMobileDetailOpen ? "hidden md:flex" : "flex"
          } flex-col w-full md:w-80 lg:w-96 border-r border-border bg-muted/30`}
        >
          <div className="p-4 border-b border-border bg-card">
            <div className="relative">
              <Search20Regular className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-secondary-text" />
              <input
                type="text"
                placeholder="Procurar fornecedores..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20"
                style={{ borderRadius: "var(--radius-button)" }}
              />
            </div>

            <div className="flex flex-wrap gap-2 mt-3">
              {filterOptions.map((option) => (
                <button
                  key={option}
                  onClick={() => setSelectedFilter(option)}
                  className={`px-3 py-1.5 rounded transition-colors ${
                    selectedFilter === option
                      ? "bg-primary text-primary-foreground"
                      : "bg-secondary hover:bg-accent border border-border text-foreground"
                  }`}
                  style={{
                    borderRadius: "var(--radius)",
                    fontSize: "var(--text-caption)",
                    fontWeight:
                      selectedFilter === option
                        ? "var(--font-weight-semi-bold)"
                        : "var(--font-weight-normal)",
                  }}
                >
                  {option}
                </button>
              ))}
            </div>

            <div className="text-sm text-[var(--muted-foreground)] mt-3">
              {isLoading
                ? "A carregar fornecedores..."
                : `${filteredFornecedores.length} fornecedor${
                    filteredFornecedores.length !== 1 ? "es" : ""
                  } encontrado${
                    filteredFornecedores.length !== 1 ? "s" : ""
                  }`}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
            {isLoading && (
              <div className="flex flex-col items-center justify-center py-12 px-4">
                <BuildingShop24Regular className="text-[var(--muted-foreground)] mb-4" />
                <p className="text-[var(--muted-foreground)] text-center">
                  A carregar fornecedores...
                </p>
              </div>
            )}

            {!isLoading && loadError && (
              <div className="flex flex-col items-center justify-center py-12 px-4">
                <BuildingShop24Regular className="text-[var(--muted-foreground)] mb-4" />
                <p className="text-[var(--destructive)] text-center">
                  {loadError}
                </p>
              </div>
            )}

            {!isLoading &&
              !loadError &&
              filteredFornecedores.map((fornecedor) => (
                <button
                  key={fornecedor.id}
                  onClick={() =>
                    handleFornecedorSelect(fornecedor.id)
                  }
                  className={`w-full text-left p-4 border-b border-border transition-colors relative ${
                    selectedFornecedorId === fornecedor.id
                      ? "bg-accent/50"
                      : ""
                  }`}
                  style={{
                    backgroundColor:
                      selectedFornecedorId === fornecedor.id
                        ? "var(--accent)"
                        : "var(--card)",
                  }}
                  onMouseEnter={(e) => {
                    if (selectedFornecedorId !== fornecedor.id) {
                      e.currentTarget.style.backgroundColor =
                        "var(--accent)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedFornecedorId !== fornecedor.id) {
                      e.currentTarget.style.backgroundColor =
                        "var(--card)";
                    }
                  }}
                >
                  {selectedFornecedorId === fornecedor.id && (
                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-primary" />
                  )}

                  <div className="flex items-start gap-3 pl-2">
                    {fornecedor.logo ? (
                      <img
                        src={fornecedor.logo}
                        alt={fornecedor.nome}
                        className="w-10 h-10 object-contain flex-shrink-0"
                        style={{
                          borderRadius: "var(--radius)",
                        }}
                      />
                    ) : (
                      <div
                        className="w-10 h-10 bg-primary/10 flex items-center justify-center flex-shrink-0"
                        style={{
                          fontSize: "var(--text-caption)",
                          fontWeight:
                            "var(--font-weight-semi-bold)",
                          color: "var(--primary)",
                          borderRadius: "var(--radius)",
                        }}
                      >
                        {getInitials(fornecedor.nome)}
                      </div>
                    )}

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p
                          className="truncate"
                          style={{
                            fontWeight:
                              "var(--font-weight-semi-bold)",
                          }}
                        >
                          {fornecedor.nome}
                        </p>
                      </div>

                      <div className="mt-1">
                        <span
                          className={`inline-flex px-2 py-0.5 rounded ${getCategoriaBadgeClass(fornecedor.categoria)}`}
                          style={{
                            fontSize: "var(--text-caption)",
                            borderRadius: "var(--radius)",
                          }}
                        >
                          {formatOptional(fornecedor.categoria)}
                        </span>
                      </div>

                      <p
                        className="text-secondary-text mt-1"
                        style={{
                          fontSize: "var(--text-caption)",
                        }}
                      >
                        {fornecedor.comprasRealizadas} compras
                        realizadas
                      </p>
                    </div>
                  </div>
                </button>
              ))}

            {!isLoading &&
              !loadError &&
              filteredFornecedores.length === 0 && (
                <div className="p-8 text-center text-secondary-text">
                  <p>Nenhum fornecedor encontrado</p>
                </div>
              )}
          </div>
        </div>

        <div
          className={`${
            isMobileDetailOpen ? "flex" : "hidden md:flex"
          } flex-1 flex-col overflow-y-auto bg-background`}
        >
          {!selectedFornecedor ? (
            <div className="flex-1 flex items-center justify-center p-8">
              <div className="text-center max-w-md">
                <BuildingShop24Regular className="w-16 h-16 mx-auto text-secondary-text mb-4" />
                <p
                  className="text-secondary-text"
                  style={{ fontSize: "var(--text-base)" }}
                >
                  Selecione um fornecedor para ver os detalhes
                </p>
              </div>
            </div>
          ) : (
            <div className="p-4 md:p-6 space-y-6">
              <button
                onClick={handleMobileBack}
                className="md:hidden flex items-center gap-2 text-secondary-text hover:text-foreground transition-colors mb-4"
              >
                <ArrowLeft24Regular className="w-6 h-6" />
                <span>Voltar</span>
              </button>

              <div
                className="bg-card border border-border p-6"
                style={{ borderRadius: "var(--radius-card)" }}
              >
                <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                  <div className="flex items-start gap-4">
                    {selectedFornecedor.logo ? (
                      <img
                        src={selectedFornecedor.logo}
                        alt={selectedFornecedor.nome}
                        className="w-16 h-16 object-contain flex-shrink-0"
                        style={{
                          borderRadius: "var(--radius)",
                        }}
                      />
                    ) : (
                      <div
                        className="w-16 h-16 bg-primary/10 flex items-center justify-center flex-shrink-0"
                        style={{
                          fontSize: "var(--text-large)",
                          fontWeight:
                            "var(--font-weight-semi-bold)",
                          color: "var(--primary)",
                          borderRadius: "var(--radius)",
                        }}
                      >
                        {getInitials(selectedFornecedor.nome)}
                      </div>
                    )}

                    <div>
                      <h3
                        className="text-foreground"
                        style={{
                          fontSize: "var(--text-large)",
                          fontWeight:
                            "var(--font-weight-semi-bold)",
                          lineHeight: "1.3",
                        }}
                      >
                        {selectedFornecedor.nome}
                      </h3>

                      <div className="flex items-center gap-2 mt-2 flex-wrap">
                        <span
                          className={`inline-flex px-2 py-1 rounded ${getCategoriaBadgeClass(selectedFornecedor.categoria)}`}
                          style={{
                            fontSize: "var(--text-caption)",
                            borderRadius: "var(--radius)",
                          }}
                        >
                          {formatOptional(selectedFornecedor.categoria)}
                        </span>
                        <span
                          className="inline-flex px-2 py-1 bg-accent text-accent-foreground rounded"
                          style={{
                            fontSize: "var(--text-caption)",
                            borderRadius: "var(--radius)",
                          }}
                        >
                          {selectedFornecedor.comprasRealizadas}{" "}
                          compras
                        </span>
                      </div>

                      <div className="mt-3 space-y-1">
                        <p
                          className="text-secondary-text"
                          style={{
                            fontSize: "var(--text-caption)",
                          }}
                        >
                          Contacto Principal
                        </p>
                        <p
                          style={{
                            fontWeight:
                              "var(--font-weight-semi-bold)",
                          }}
                        >
                          {formatOptional(selectedFornecedor.contacto)}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <button
                      className="px-4 py-2 bg-secondary hover:bg-accent border border-border rounded transition-colors flex items-center gap-2 min-h-[44px]"
                      style={{
                        borderRadius: "var(--radius-button)",
                      }}
                      onClick={() =>
                        navigate(
                          `/confirming?fornecedor=${encodeURIComponent(
                            selectedFornecedor.nome,
                          )}`,
                        )
                      }
                      title="Ver os APs de Confirming deste fornecedor"
                    >
                      <BuildingBank20Regular className="w-5 h-5" />
                      <span>Ver Confirming</span>
                    </button>
                    <button
                      className="px-4 py-2 bg-secondary hover:bg-accent border border-border rounded transition-colors flex items-center gap-2 min-h-[44px]"
                      style={{
                        borderRadius: "var(--radius-button)",
                      }}
                      onClick={() =>
                        navigate(
                          `/fornecedores/editar/${selectedFornecedor.id}`,
                        )
                      }
                    >
                      <Edit20Regular className="w-5 h-5" />
                      <span>Editar Fornecedor</span>
                    </button>
                  </div>
                </div>
              </div>

              <div
                className="bg-card border border-border p-6 space-y-4"
                style={{ borderRadius: "var(--radius-card)" }}
              >
                <h4
                  className="text-foreground"
                  style={{
                    fontSize: "var(--text-section-title)",
                    fontWeight: "var(--font-weight-semi-bold)",
                  }}
                >
                  Informação de Contacto
                </h4>

                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <Mail24Regular className="w-5 h-5 text-secondary-text flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p
                        className="text-secondary-text"
                        style={{
                          fontSize: "var(--text-caption)",
                        }}
                      >
                        Email
                      </p>
                      <p className="mt-0.5">
                        {formatOptional(selectedFornecedor.email)}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <Call24Regular className="w-5 h-5 text-secondary-text flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p
                        className="text-secondary-text"
                        style={{
                          fontSize: "var(--text-caption)",
                        }}
                      >
                        Telefone
                      </p>
                      <p className="mt-0.5">
                        {formatOptional(selectedFornecedor.telefone)}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <TextBulletListSquare24Regular className="w-5 h-5 text-secondary-text flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p
                        className="text-secondary-text"
                        style={{
                          fontSize: "var(--text-caption)",
                        }}
                      >
                        Condições Comerciais
                      </p>
                      <p className="mt-0.5">
                        {formatOptional(
                          selectedFornecedor.condicoes,
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div
                className="bg-card border border-border p-6 space-y-4"
                style={{ borderRadius: "var(--radius-card)" }}
              >
                <h4
                  className="text-foreground"
                  style={{
                    fontSize: "var(--text-section-title)",
                    fontWeight: "var(--font-weight-semi-bold)",
                  }}
                >
                  Compras Recentes
                </h4>

                <div className="space-y-3">
                  <div
                    className="flex items-center justify-between p-3 bg-muted/30 rounded border border-border"
                    style={{ borderRadius: "var(--radius)" }}
                  >
                    <div className="flex-1">
                      <p
                        style={{
                          fontWeight:
                            "var(--font-weight-semi-bold)",
                        }}
                      >
                        PC-2024-089
                      </p>
                      <p
                        className="text-secondary-text mt-0.5"
                        style={{
                          fontSize: "var(--text-caption)",
                        }}
                      >
                        05/12/2024
                      </p>
                    </div>
                    <p
                      style={{
                        fontWeight:
                          "var(--font-weight-semi-bold)",
                      }}
                    >
                      12 450,00 €
                    </p>
                  </div>
                  <p
                    className="text-secondary-text text-center py-2"
                    style={{ fontSize: "var(--text-caption)" }}
                  >
                    Ver histórico completo de compras
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <ExportarFornecedoresModal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        totalFornecedoresFiltrados={filteredFornecedores.length}
      />

      {isDuplicatesModalOpen && (
        <DuplicateSuppliersModal
          onClose={() => setIsDuplicatesModalOpen(false)}
          onMerged={loadSuppliers}
        />
      )}
    </div>
  );
}
